import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import {
  Building2,
  Calculator,
  Shield,
  Globe,
  Plane,
  GitMerge,
  ArrowRight,
  CheckCircle,
  MapPin,
  Users,
  TrendingUp,
  FileCheck,
  ChevronRight,
  GraduationCap,
  Cpu,
  Banknote,
  Navigation,
} from "lucide-react";

const HomePage = () => {
  const openCalendly = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: "https://calendly.com/franjo-vucic-d0pg/30min",
      });
    }
  };

  const coreServices = [
    {
      icon: Building2,
      title: "Corporate Services",
      description: "Company formation, governance and corporate administration in Bosnia and Herzegovina.",
      link: "/services/corporate",
    },
    {
      icon: Calculator,
      title: "Accounting & Tax",
      description: "Financial operations, compliance and tax coordination for businesses in BiH.",
      link: "/services/accounting",
    },
    {
      icon: Shield,
      title: "Trusts & Fiduciary",
      description: "Escrow services, fiduciary arrangements and asset protection structures.",
      link: "/services/trusts",
    },
    {
      icon: Globe,
      title: "Citizenship & Residence",
      description: "Residence permits and relocation planning for professionals moving to BiH.",
      link: "/services/citizenship",
    },
    {
      icon: Plane,
      title: "Business Relocation",
      description: "Corporate relocation and operational setup for companies entering BiH.",
      link: "/services/relocation",
      highlighted: true,
    },
    {
      icon: GitMerge,
      title: "M&A Advisory",
      description: "Transaction coordination, due diligence and restructuring support.",
      link: "/services/ma",
    },
  ];

  const whyBiH = [
    { icon: MapPin, title: "Strategic Location", description: "Gateway to Western Balkans and Southeast Europe" },
    { icon: TrendingUp, title: "Competitive Costs", description: "Lower operational costs vs. EU markets" },
    { icon: Users, title: "Skilled Workforce", description: "Educated, multilingual professionals" },
    { icon: FileCheck, title: "10% Corporate Tax", description: "One of the lowest rates in Europe" },
  ];

  const whyMostar = [
    { icon: Globe, title: "EU Proximity", description: "Direct access to Croatia and EU markets" },
    { icon: GraduationCap, title: "Skilled Talent", description: "Universities producing tech & business professionals" },
    { icon: Banknote, title: "Cost Efficient", description: "Competitive operational and administrative costs" },
    { icon: Cpu, title: "Tech Ecosystem", description: "Growing technology and service industries" },
    { icon: Navigation, title: "Regional Hub", description: "Excellent connectivity to Western Balkans" },
  ];

  const industries = [
    "Technology Companies",
    "Fintech & Financial Services",
    "International Investors",
    "Family Offices",
    "Professional Services",
    "Platform Businesses",
  ];

  return (
    <div data-testid="home-page" className="pt-16 md:pt-20">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative bg-gradient-to-b from-[#F5F7FA] to-white py-20 md:py-28 lg:py-36"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="text-left">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-[#2F6FED]" />
                <p className="text-sm text-[#2F6FED] uppercase tracking-widest font-semibold">
                  Mostar, Bosnia and Herzegovina
                </p>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.1] text-[#0B2A4A] mb-6 font-['Sora']">
                Your Gateway to Bosnia and Herzegovina
              </h1>
              <p className="text-lg md:text-xl leading-relaxed text-slate-600 mb-8 max-w-xl">
                VTZ Backoffice provides corporate and operational infrastructure for 
                international companies entering Bosnia and Herzegovina.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  data-testid="hero-cta-book-call"
                  onClick={openCalendly}
                  className="bg-[#2F6FED] hover:bg-[#2558be] text-white font-medium px-8 py-4 rounded-md transition-all duration-200 shadow-sm hover:shadow-md text-base"
                >
                  Schedule a Call
                </Button>
                <Link to="/doing-business-bosnia">
                  <Button
                    data-testid="hero-cta-guide"
                    variant="outline"
                    className="border-2 border-[#0B2A4A] text-[#0B2A4A] hover:bg-[#0B2A4A] hover:text-white font-medium px-8 py-4 rounded-md transition-all duration-200 w-full sm:w-auto text-base"
                  >
                    Doing Business in BiH
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-slate-100">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  <span className="ml-auto text-xs text-slate-400">VTZ Dashboard</span>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-[#F5F7FA] rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#2F6FED]/10 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-[#2F6FED]" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#0B2A4A]">Company Setup</p>
                        <p className="text-xs text-slate-500">Bosnia and Herzegovina</p>
                      </div>
                    </div>
                    <div className="text-green-500 text-sm font-medium">Active</div>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-[#F5F7FA] rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#2F6FED]/10 rounded-lg flex items-center justify-center">
                        <Shield className="w-5 h-5 text-[#2F6FED]" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#0B2A4A]">Compliance Status</p>
                        <p className="text-xs text-slate-500">All filings current</p>
                      </div>
                    </div>
                    <div className="text-green-500 text-sm font-medium">100%</div>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-[#2F6FED] rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">Headquarters</p>
                        <p className="text-xs text-white/70">Mostar, BiH</p>
                      </div>
                    </div>
                    <div className="text-white text-sm font-medium">HQ</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why BiH Section */}
      <section data-testid="why-bih-section" className="py-16 md:py-20 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm text-[#2F6FED] uppercase tracking-widest font-semibold mb-4">
              Why Bosnia and Herzegovina
            </p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-[#0B2A4A] font-['Sora']">
              A Strategic Business Destination
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyBiH.map((item, index) => (
              <div key={index} className="text-center p-6">
                <div className="w-14 h-14 bg-[#2F6FED]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-[#2F6FED]" />
                </div>
                <h3 className="text-lg font-semibold text-[#0B2A4A] mb-2 font-['Sora']">
                  {item.title}
                </h3>
                <p className="text-sm text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/doing-business-bosnia">
              <Button variant="outline" className="border-[#0B2A4A] text-[#0B2A4A] hover:bg-[#0B2A4A] hover:text-white">
                Learn More About BiH
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Mostar Section */}
      <section data-testid="why-mostar-section" className="py-20 md:py-28 bg-[#0B2A4A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-[#2F6FED]" />
                <p className="text-sm text-[#2F6FED] uppercase tracking-widest font-semibold">
                  Our Headquarters
                </p>
              </div>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-6 font-['Sora']">
                Why Our Headquarters Are in Mostar
              </h2>
              <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                VTZ Backoffice is headquartered in Mostar, one of the most strategically 
                positioned cities in Bosnia and Herzegovina. Located in the south of the 
                country, Mostar offers direct proximity to the European Union through 
                neighboring Croatia and provides an efficient operational base for 
                companies expanding across the Western Balkans.
              </p>
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                The city combines a growing technology and service ecosystem with lower 
                operational costs and access to a highly skilled multilingual workforce. 
                For international companies seeking a reliable regional base, Mostar offers 
                a unique balance of connectivity, talent and efficiency.
              </p>
              <Link to="/doing-business-bosnia">
                <Button className="bg-[#2F6FED] hover:bg-[#2558be] text-white font-medium px-6 py-3 rounded-md">
                  Explore Business Opportunities in BiH
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {whyMostar.map((item, index) => (
                  <div
                    key={index}
                    className="bg-white/5 backdrop-blur rounded-xl p-5 border border-white/10"
                  >
                    <div className="w-10 h-10 bg-[#2F6FED]/20 rounded-lg flex items-center justify-center mb-3">
                      <item.icon className="w-5 h-5 text-[#2F6FED]" />
                    </div>
                    <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                    <p className="text-slate-400 text-sm">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section
        data-testid="services-section"
        className="py-20 md:py-28 bg-[#F5F7FA]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm text-[#2F6FED] uppercase tracking-widest font-semibold mb-4">
              Our Services
            </p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0B2A4A] font-['Sora']">
              Corporate Services in Bosnia and Herzegovina
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coreServices.map((service, index) => (
              <Link key={index} to={service.link}>
                <Card
                  data-testid={`service-card-${index}`}
                  className={`h-full bg-white rounded-xl border transition-all duration-300 hover:-translate-y-1 hover:shadow-lg group cursor-pointer ${
                    service.highlighted
                      ? "border-[#2F6FED] shadow-[0_0_20px_-5px_rgba(47,111,237,0.3)]"
                      : "border-slate-100"
                  }`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        service.highlighted ? "bg-[#2F6FED]" : "bg-[#2F6FED]/10"
                      }`}>
                        <service.icon className={`w-6 h-6 ${
                          service.highlighted ? "text-white" : "text-[#2F6FED]"
                        }`} />
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-[#2F6FED] group-hover:translate-x-1 transition-all" />
                    </div>
                    {service.highlighted && (
                      <span className="inline-block text-xs font-semibold text-[#2F6FED] bg-[#2F6FED]/10 px-2 py-1 rounded mb-3">
                        Popular Service
                      </span>
                    )}
                    <h3 className="text-lg font-semibold text-[#0B2A4A] mb-2 font-['Sora'] group-hover:text-[#2F6FED] transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-sm text-slate-600">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/services">
              <Button
                data-testid="view-all-services-btn"
                className="bg-[#2F6FED] hover:bg-[#2558be] text-white font-medium px-6 py-3 rounded-md transition-all duration-200"
              >
                View All Services
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section data-testid="industries-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm text-[#2F6FED] uppercase tracking-widest font-semibold mb-4">
                Industries We Serve
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0B2A4A] mb-6 font-['Sora']">
                Supporting International Business
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                VTZ Backoffice provides specialized services for international companies 
                across various industries entering Bosnia and Herzegovina.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                {industries.map((industry, index) => (
                  <span
                    key={index}
                    className="bg-[#F5F7FA] text-slate-600 text-sm px-4 py-2 rounded-full"
                  >
                    {industry}
                  </span>
                ))}
              </div>
              <Link to="/industries">
                <Button className="bg-[#0B2A4A] hover:bg-[#0B2A4A]/90 text-white font-medium px-6 py-3 rounded-md">
                  View All Industries
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="bg-[#F5F7FA] rounded-xl p-8">
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
                    <CheckCircle className="w-6 h-6 text-[#2F6FED]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0B2A4A]">Full-Service Support</p>
                    <p className="text-sm text-slate-500">From setup to ongoing operations</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
                    <CheckCircle className="w-6 h-6 text-[#2F6FED]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0B2A4A]">Local Expertise</p>
                    <p className="text-sm text-slate-500">Deep understanding of BiH market</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
                    <CheckCircle className="w-6 h-6 text-[#2F6FED]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0B2A4A]">International Standards</p>
                    <p className="text-sm text-slate-500">Professional service delivery</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Doing Business Guide CTA */}
      <section className="py-16 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm text-[#2F6FED] uppercase tracking-widest font-semibold mb-4">
                Investment Guide
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0B2A4A] mb-6 font-['Sora']">
                Doing Business in Bosnia and Herzegovina
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Our comprehensive guide covers everything you need to know about 
                establishing and operating a business in Bosnia and Herzegovina.
              </p>
              <Link to="/doing-business-bosnia">
                <Button className="bg-[#0B2A4A] hover:bg-[#0B2A4A]/90 text-white font-medium px-6 py-3 rounded-md">
                  Read the Full Guide
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#2F6FED]" />
                    <span className="text-slate-700">Company Formation Process</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#2F6FED]" />
                    <span className="text-slate-700">Tax Environment Overview</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#2F6FED]" />
                    <span className="text-slate-700">Employment Requirements</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#2F6FED]" />
                    <span className="text-slate-700">Compliance Obligations</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#2F6FED]" />
                    <span className="text-slate-700">Why Mostar as Operational Hub</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        data-testid="final-cta-section"
        className="py-20 md:py-28 bg-[#0B2A4A]"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white mb-6 font-['Sora']">
            Ready to Enter Bosnia and Herzegovina?
          </h2>
          <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            Contact VTZ Backoffice in Mostar to discuss your corporate structuring, 
            compliance and operational requirements in Bosnia and Herzegovina.
          </p>
          <Button
            data-testid="final-cta-book-call"
            onClick={openCalendly}
            className="bg-[#2F6FED] hover:bg-[#2558be] text-white font-medium px-10 py-5 rounded-md transition-all duration-200 shadow-lg hover:shadow-xl text-lg"
          >
            Schedule a Consultation
          </Button>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
